import {
  collection, doc, getDoc,
  query, orderBy, limit as limitQuery,
  writeBatch, serverTimestamp, arrayUnion, updateDoc,
  onSnapshot,
} from 'firebase/firestore'
import { db } from './firebase'
import { sendEmail, sendLeadEmail as sendLeadEmailApi  } from "../services/emailService"

export const sendLeadEmail = async (organizationId, applicantId, metadata = {}) => {
  try {
    const conversationId = [organizationId, applicantId].sort().join('-')
    const senderType = 'organization'

    const conversationRef = doc(db, 'conversations', conversationId)
    const conversationSnap = await getDoc(conversationRef)

    if (!conversationSnap.exists()) {
      await initializeConversation(organizationId, applicantId)
    }

    const batch = writeBatch(db)

    const messageData = {
      text: metadata.text,
      organizationId,
      senderType,
      timestamp: serverTimestamp(),
      read: false,
    }

    const messageRef = doc(collection(db, 'conversations', conversationId, 'messages'))
    batch.set(messageRef, messageData)

    batch.set(conversationRef, {
      lastMessage: { text: metadata.text, organizationId, senderType, timestamp: serverTimestamp() },
      lastMessageAt: serverTimestamp(),
    }, { merge: true })

    await batch.commit()

    await sendLeadEmailApi({
      email:          metadata.candidateEmail,
      subject:        metadata.subject,
      text:           metadata.text,
      html:           metadata.html,
      conversationId,
      senderName:     metadata.organizationName,
      orgId:          organizationId,
      jobId:          metadata.jobId,
      applicantId,
    })

    return { success: true, messageId: messageRef.id, conversationId }
  } catch (error) {
    console.error('Error sending lead email:', error)
    throw error
  }
}

export const sendMessage = async (organizationId, applicantId, text, senderId) => {
  try {
    const conversationId = [organizationId, applicantId].sort().join('-')
    const senderType = senderId === organizationId ? 'organization' : 'applicant'

    const conversationRef = doc(db, 'conversations', conversationId)
    const conversationSnap = await getDoc(conversationRef)

    if (!conversationSnap.exists()) {
      await initializeConversation(organizationId, applicantId)
    }

    const batch = writeBatch(db)

    const messageData = {
      text,
      senderId,
      senderType,
      timestamp: serverTimestamp(),
      read: false,
    }

    const messageRef = doc(collection(db, 'conversations', conversationId, 'messages'))
    batch.set(messageRef, messageData)

    batch.set(conversationRef, {
      lastMessage: { text, senderId, senderType, timestamp: serverTimestamp() },
      lastMessageAt: serverTimestamp(),
    }, { merge: true })

    await batch.commit()

    await sendMessageNotification(conversationId, text, senderType)

    return { success: true, messageId: messageRef.id, conversationId }
  } catch (error) {
    console.error('Error sending message:', error)
    throw error
  }
}

export const initializeConversation = async (organizationId, applicantId) => {
  try {
    const conversationId = [organizationId, applicantId].sort().join('-')
    const conversationRef = doc(db, 'conversations', conversationId)
    const conversationSnap = await getDoc(conversationRef)

    if (conversationSnap.exists()) {
      return conversationId
    }

    const batch = writeBatch(db)

    const [orgSnap, applicantSnap] = await Promise.all([
      getDoc(doc(db, 'organizations', organizationId)),
      getDoc(doc(db, 'users', applicantId)),
    ])

    const orgData = orgSnap.exists() ? orgSnap.data() : {}
    const applicantData = applicantSnap.exists() ? applicantSnap.data() : {}

    batch.set(conversationRef, {
      id: conversationId,
      organizationId,
      applicantId,
      participants: [organizationId, applicantId],
      metadata: {
        organizationName: orgData.name || '',
        logoUrl: orgData.logoUrl || '',
        candidateName: applicantData.name || '',
        candidateEmail: applicantData.email || '',
        candidateAvatar:  ''
      },
      createdAt: serverTimestamp(),
      lastMessageAt: serverTimestamp(),
    })

    const orgRef = doc(db, 'organizations', organizationId)
    batch.set(orgRef, { conversationIds: arrayUnion(conversationId) }, { merge: true })

    const applicantRef = doc(db, 'users', applicantId)
    batch.set(applicantRef, { conversationIds: arrayUnion(conversationId) }, { merge: true })

    await batch.commit()
    return conversationId
  } catch (error) {
    console.error('Error initializing conversation:', error)
    throw error
  }
}

export const updateConversationMetadata = async (conversationId, metadata) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId)
    const conversationSnap = await getDoc(conversationRef)

    if (!conversationSnap.exists()) throw new Error('Conversation not found')

    const currentData = conversationSnap.data()
    const currentMetadata = currentData.metadata || {}

    await updateDoc(conversationRef, { metadata: { ...currentMetadata, ...metadata } })
  } catch (error) {
    console.error('Error updating conversation metadata:', error)
    throw error
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ONE-TIME FETCHES  (unchanged — used for initial load)
// ─────────────────────────────────────────────────────────────────────────────

export const getOrganizationConversations = async (organizationId) => {
  try {
    const orgSnap = await getDoc(doc(db, 'organizations', organizationId))
    if (!orgSnap.exists()) return []

    const conversationIds = orgSnap.data().conversationIds || []
    if (conversationIds.length === 0) return []

    const snaps = await Promise.all(
      conversationIds.map((id) => getDoc(doc(db, 'conversations', id)))
    )

    return snaps
      .filter((s) => s.exists())
      .map((s) => ({ id: s.id, ...s.data() }))
      .sort((a, b) => (b.lastMessageAt?.toMillis() || 0) - (a.lastMessageAt?.toMillis() || 0))
  } catch (error) {
    console.error('Error fetching organization conversations:', error)
    throw error
  }
}

export const getApplicantConversations = async (applicantId) => {
  try {
    const applicantSnap = await getDoc(doc(db, 'users', applicantId))
    if (!applicantSnap.exists()) return []

    const conversationIds = applicantSnap.data().conversationIds || []
    if (conversationIds.length === 0) return []

    const snaps = await Promise.all(
      conversationIds.map((id) => getDoc(doc(db, 'conversations', id)))
    )

    return snaps
      .filter((s) => s.exists())
      .map((s) => ({ id: s.id, ...s.data() }))
      .sort((a, b) => (b.lastMessageAt?.toMillis() || 0) - (a.lastMessageAt?.toMillis() || 0))
  } catch (error) {
    console.error('Error fetching applicant conversations:', error)
    throw error
  }
}


export const pollUnreadCount = (participantId, isOrganization, onCount, onError) => {
  const fetchCount = async () => {
    try {
      const conversations = isOrganization
        ? await getOrganizationConversations(participantId)
        : await getApplicantConversations(participantId)

      const count = conversations.filter((c) => {
        if (!c.lastMessage) return false
        // Only count as unread if the other party sent the last message AND it's unread
        const sentByOtherParty = isOrganization
          ? c.lastMessage.senderType === 'applicant'
          : c.lastMessage.senderType === 'organization'
        return sentByOtherParty && c.lastMessage.read !== true
      }).length

      onCount(count)
    } catch (error) {
      console.error('pollUnreadCount error:', error)
      onError?.(error)
    }
  }

  fetchCount()
  const interval = setInterval(fetchCount, 60_000)

  return () => clearInterval(interval)
}

// ─────────────────────────────────────────────────────────────────────────────
// REAL-TIME: watch the user/org doc for new conversationIds
//
// Returns an unsubscribe function.
// onNewIds(addedIds: string[]) is called with only the brand-new IDs so the
// caller can fetch those docs and attach per-conversation listeners to them.
// ─────────────────────────────────────────────────────────────────────────────

export const subscribeToConversationIds = (participantId, isOrganization, onNewIds, onError) => {
  const docRef = isOrganization
    ? doc(db, 'organizations', participantId)
    : doc(db, 'users', participantId)

  let knownIds = null  // null = first snapshot (initialization)

  return onSnapshot(
    docRef,
    (snap) => {
      if (!snap.exists()) return
      const ids = snap.data().conversationIds || []

      if (knownIds === null) {
        // First snapshot — just record what we already have from the initial fetch.
        // Don't call onNewIds here; the fetch already handled these.
        knownIds = new Set(ids)
        return
      }

      const added = ids.filter((id) => !knownIds.has(id))
      if (added.length > 0) {
        added.forEach((id) => knownIds.add(id))
        onNewIds(added)
      }
    },
    (error) => {
      console.error('subscribeToConversationIds error:', error)
      onError?.(error)
    },
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// REAL-TIME: watch a single conversation doc for lastMessage / lastMessageAt
//
// Returns an unsubscribe function.
// onUpdate(updatedConversation) fires on every change.
// ─────────────────────────────────────────────────────────────────────────────

export const subscribeToConversation = (conversationId, onUpdate, onError) => {
  const conversationRef = doc(db, 'conversations', conversationId)

  return onSnapshot(
    conversationRef,
    (snap) => {
      if (!snap.exists()) return
      onUpdate({ id: snap.id, ...snap.data() })
    },
    (error) => {
      console.error(`subscribeToConversation(${conversationId}) error:`, error)
      onError?.(error)
    },
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// REAL-TIME: watch the messages subcollection of a conversation
//
// Returns an unsubscribe function.
// onUpdate(enrichedMessages, metadata) fires whenever any message is added/changed.
// ─────────────────────────────────────────────────────────────────────────────

export const subscribeToMessages = (conversationId, onUpdate, onError) => {
  const conversationRef = doc(db, 'conversations', conversationId)
  const messagesRef = collection(db, 'conversations', conversationId, 'messages')
  const q = query(messagesRef, orderBy('timestamp', 'asc'), limitQuery(50))

  // We need metadata to enrich messages — fetch it once up front then reuse.
  // If metadata itself ever changes we pick that up via subscribeToConversation.
  let cachedMetadata = null

  const getMetadata = async () => {
    if (cachedMetadata) return cachedMetadata
    const snap = await getDoc(conversationRef)
    cachedMetadata = snap.exists() ? (snap.data().metadata || {}) : {}
    return cachedMetadata
  }

  const unsub = onSnapshot(
    q,
    async (snapshot) => {
      try {
        const metadata = await getMetadata()

        const messages = snapshot.docs.map((d) => {
          const data = d.data()
          return {
            id: d.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date(),
            senderName: data.senderType === 'organization'
              ? metadata.organizationName
              : metadata.candidateName,
            senderAvatar: data.senderType === 'organization'
              ? metadata.logoUrl
              : metadata.candidateAvatar,
          }
        })

        onUpdate(messages, metadata)
      } catch (error) {
        console.error(`subscribeToMessages(${conversationId}) enrichment error:`, error)
        onError?.(error)
      }
    },
    (error) => {
      console.error(`subscribeToMessages(${conversationId}) error:`, error)
      onError?.(error)
    },
  )

  return unsub
}

// ─────────────────────────────────────────────────────────────────────────────
// MARK AS READ  (unchanged)
// ─────────────────────────────────────────────────────────────────────────────

export const markMessagesAsRead = async (conversationId, userId) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId)
    const conversationSnap = await getDoc(conversationRef)
    if (!conversationSnap.exists()) return

    const conversationData = conversationSnap.data()
    if (
      conversationData.lastMessage &&
      conversationData.lastMessage.senderId !== userId
    ) {
      await updateDoc(conversationRef, { 'lastMessage.read': true })
    }
  } catch (error) {
    console.error('Error marking messages as read:', error)
    throw error
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// EMAIL NOTIFICATION  (unchanged)
// ─────────────────────────────────────────────────────────────────────────────

const sendMessageNotification = async (conversationId, messageText, senderType) => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId)
    const conversationSnap = await getDoc(conversationRef)
    if (!conversationSnap.exists()) return

    const conversationData = conversationSnap.data()
    const metadata = conversationData.metadata || {}

    if (senderType === 'organization' && metadata.candidateEmail) {
      const htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h3>New message from ${metadata.organizationName}</h3>
          <div style="background: #f5f5f5; padding: 20px; border-radius: 5px; margin: 20px 0;">
            <p><strong>${metadata.organizationName}</strong></p>
            <p style="white-space: pre-wrap;">${messageText}</p>
          </div>
          <a href="https://resolut.co.in/inbox"
             style="display: inline-block; background: #007bff; color: white;
                    padding: 12px 24px; text-decoration: none; border-radius: 5px; margin: 20px 0;">
            View on Resolut
          </a>
          <p style="color: #666; font-size: 14px; margin-top: 20px;">Or reply directly to this email</p>
          <hr style="border: none; border-top: 1px solid #ddd; margin: 30px 0;">
          <p style="color: #999; font-size: 12px;">
            This email was sent from Resolut.
            <a href="https://resolut.co.in" style="color: #007bff;">Visit our website</a>
          </p>
        </div>`

      await sendEmail({
        email: metadata.candidateEmail,
        subject: `New message from ${metadata.organizationName} on Resolut`,
        text: `${metadata.organizationName}: ${messageText}\n\nReply at: https://resolut.co.in/inbox`,
        html: htmlContent,
        conversationId,
        recipientEmail: metadata.candidateEmail,
        senderName: metadata.organizationName,
      })
    } else if (senderType === 'applicant') {
      const orgSnap = await getDoc(doc(db, 'organizations', conversationData.organizationId))
      if (!orgSnap.exists()) return

      const orgData = orgSnap.data()
      const organizationEmail = orgData.email || orgData.contactEmail
      if (!organizationEmail) return

      const htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h3>New message from ${metadata.candidateName}</h3>
          <div style="background: #f5f5f5; padding: 20px; border-radius: 5px; margin: 20px 0;">
            <p><strong>${metadata.candidateName}</strong></p>
            <p style="white-space: pre-wrap;">${messageText}</p>
          </div>
          <a href="https://resolut.co.in/inbox"
             style="display: inline-block; background: #007bff; color: white;
                    padding: 12px 24px; text-decoration: none; border-radius: 5px; margin: 20px 0;">
            View on Resolut
          </a>
          <p style="color: #666; font-size: 14px; margin-top: 20px;">Or reply directly to this email</p>
          <hr style="border: none; border-top: 1px solid #ddd; margin: 30px 0;">
          <p style="color: #999; font-size: 12px;">
            This email was sent from Resolut.
            <a href="https://resolut.co.in" style="color: #007bff;">Visit our website</a>
          </p>
        </div>`

      await sendEmail({
        email: organizationEmail,
        subject: `New message from ${metadata.candidateName} on Resolut`,
        text: `${metadata.candidateName}: ${messageText}\n\nReply at: https://resolut.co.in/inbox`,
        html: htmlContent,
        conversationId,
        recipientEmail: organizationEmail,
        senderName: metadata.candidateName,
      })
    }
  } catch (error) {
    console.error('Error sending email notification:', error)
  }
}